/**
 * DataTableMobileCard Component
 *
 * Renders a card view for mobile devices instead of a table row.
 * Shows primary column as title, secondary columns as details, and actions.
 */

import React, { ReactNode } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ColumnDefinition, ActionDefinition } from './types';
import { staggerDelay } from './stagger';
import { ActionButtons } from './ActionButtons';

interface DataTableMobileCardProps<T> {
  row: T;
  index: number;
  columns: ColumnDefinition<T>[];
  actions?: ActionDefinition<T>[];
  rowId: string;
  selectable?: boolean;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  expandable?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (id: string) => void;
  renderExpanded?: (row: T) => ReactNode;
  onRowExpand?: (row: T) => void;
  className?: string;
}

export function DataTableMobileCard<T>({
  row,
  index,
  columns,
  actions,
  rowId,
  selectable = false,
  isSelected = false,
  onSelect,
  expandable = false,
  isExpanded = false,
  onToggleExpand,
  renderExpanded,
  onRowExpand,
  className
}: DataTableMobileCardProps<T>) {
  const shouldReduceMotion = useReducedMotion();

  // Get primary column (first one marked as primary, or just the first column)
  const primaryColumn = columns.find(col => col.isPrimary) || columns[0];

  // Get columns to show in card (marked with showInMobileCard, or first 3 by default)
  const mobileColumns = columns.filter((col, idx) => {
    if (col.isPrimary) return false; // Primary shown as title
    if (col.showInMobileCard !== undefined) return col.showInMobileCard;
    return idx < 4; // Default: show first 4 columns (excluding primary)
  }).slice(0, 4);

  const handleToggleExpand = () => {
    if (expandable && onToggleExpand) {
      onToggleExpand(rowId);
      if (!isExpanded && onRowExpand) {
        onRowExpand(row);
      }
    }
  };

  return (
    <motion.div
      initial={shouldReduceMotion ? undefined : { opacity: 0, y: 10 }}
      animate={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
      transition={{ delay: staggerDelay(index, 0.03, shouldReduceMotion) }}
      className={`
        bg-white rounded-lg border shadow-sm overflow-hidden
        ${isSelected ? 'border-purple-400 ring-2 ring-purple-100' : 'border-gray-200'}
        ${className || ''}
      `}
    >
      {/* Card Header with Primary Column */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Selection Checkbox */}
            {selectable && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect?.(rowId)}
                className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mt-0.5 flex-shrink-0"
              />
            )}

            {/* Expand Toggle */}
            {expandable && (
              <button
                onClick={handleToggleExpand}
                className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-purple-600" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            )}

            {/* Primary Content */}
            <div className="flex-1 min-w-0">
              {primaryColumn.accessor(row)}
            </div>
          </div>

          {/* Actions */}
          {actions && actions.length > 0 && (
            <div className="flex-shrink-0">
              <ActionButtons row={row} actions={actions} />
            </div>
          )}
        </div>

        {/* Secondary Columns */}
        {mobileColumns.length > 0 && (
          <div className="mt-3 space-y-2">
            {mobileColumns.map(column => (
              <div key={column.id} className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 font-medium min-w-[80px] flex-shrink-0">
                  {column.header}:
                </span>
                <span className="text-gray-900 flex-1">
                  {column.accessor(row)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {expandable && isExpanded && renderExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-100 bg-gray-50 p-4"
          >
            {renderExpanded(row)}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default DataTableMobileCard;
